import { useQuery } from '@tanstack/react-query';
import {
  Link,
  createFileRoute,
  redirect,
  useNavigate,
  useRouterState,
} from '@tanstack/react-router';
import { FormEventHandler, SyntheticEvent } from 'react';
import { z } from 'zod';

import { FilterSchema } from '@/api';
import { useBedrijf } from '@/api/queries/bedrijf';
import { Button } from '@/components/Button/Button';
import { CompanyRowLink } from '@/components/CompanyRow/CompanyRow';
import { Disclaimer } from '@/components/Disclaimer/Disclaimer';
import { DocumentTitle } from '@/components/DocumentTitle';
import { FormLabel } from '@/components/Form/FormLabel';
import { ModalTrigger, useModalManagerStore } from '@/components/Modal';
import { Navbar } from '@/components/Navbar/Navbar';
import { PaginationLinks } from '@/components/PaginationLinks/PaginationLinks';
import { TextButton } from '@/components/TextButton/TextButton';
import { eStrings } from '@/constants/strings';
import { RegularMagnifyingGlass, RegularXmark } from '@/icons/components';
import { Message } from '@/modals/Message';
import { Search } from '@/modals/Search';
import { pick } from '@/utils/pick';
import { twJoin } from '@/utils/tw-merge';

const PAGE_SIZE = 10;

const searchSchema = z.object({
  query: z.string().optional(),
  straat: z.string().optional(),
  postcode: z.string().or(z.number()).transform(String).optional(),
  plaats: z.string().optional(),
  locatienummer: z.string().optional(),
  p: z.number().optional().default(1),
  error: z.enum(['uitgelogd']).optional().catch(undefined),
});

export type SearchParams = z.infer<typeof searchSchema>;

/**
 * Query options
 */
const bedrijfQueryOptions = (search: SearchParams) => {
  const filter: FilterSchema[] = [
    {
      field: 'Volgend',
      op: 'is_null',
      value: '',
    },
  ];

  Object.entries({
    Plaatsnaam: search.plaats,
    Straatnaam: search.straat,
    Postcode: search.postcode,
    Locatienummer: search.locatienummer,
    Naam: search.query,
  }).forEach(([field, value]) => {
    if (value) {
      filter.push({
        field,
        op: 'like',
        value: `%${value.replace(/\s+/g, '%')}%`,
      });
    }
  });

  return useBedrijf.getOptions({
    page: search.p,
    size: PAGE_SIZE,
    filter,
    sort: [
      {
        direction: 'asc',
        field: 'Naam',
      },
    ],
  });
};

/**
 * Route
 */
export const Route = createFileRoute('/')({
  validateSearch: searchSchema.parse,
  loaderDeps: ({ search }) => ({ search }),
  loader: async ({ context: { queryClient }, deps }) => {
    if (deps.search.p <= 0) {
      throw redirect({ to: '/', search: { p: 1 } });
    }

    const bedrijven = await queryClient.ensureQueryData(
      bedrijfQueryOptions(deps.search)
    );

    if (!bedrijven.num_pages) return;

    if (bedrijven.num_pages < deps.search.p) {
      throw redirect({ to: '/', search: { p: bedrijven.num_pages } });
    }
  },
  onEnter: ({ search }) => {
    if (search.error) {
      const openModal = useModalManagerStore.getState().openModal;

      switch (search.error) {
        case 'uitgelogd':
          openModal(Message, {
            title: 'Je bent uitgelogd',
            description:
              'Vanwege inactiviteit en uit veiligheidsoverwegingen ben je automatisch uitgelogd.',
          });
          break;
        default:
          break;
      }
    }
  },
  component: IndexComponent,
});

/**
 * Route component
 */
function IndexComponent() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const bedrijven = useQuery(bedrijfQueryOptions(search));
  const searchIsLoading = useRouterState({
    select: (routerState) =>
      !!(
        routerState.isLoading &&
        routerState.pendingMatches?.find((match) => match.routeId === Route.id)
      ),
  });

  function handleSubmit(e: SyntheticEvent<HTMLFormElement>) {
    e.preventDefault();

    const { value: query } = e.currentTarget[
      'search-for-company'
    ] as HTMLInputElement;

    navigate({
      to: '/',
      search: {
        query: query.length > 0 ? query : undefined,
        p: 1,
      },
    });
  }

  const hasSearch = Object.values(
    pick(search, ['straat', 'postcode', 'plaats', 'locatienummer', 'query'])
  ).some(Boolean);

  return (
    <>
      <DocumentTitle sections={[eStrings.LBL_DIGI_V, 'Zoeken']} />

      <Navbar />

      <div className="container-default mt-2">
        <SearchInput
          onSubmit={handleSubmit}
          defaultValue={search.query ?? ''}
          isLoading={searchIsLoading}
        />
      </div>

      <div className="container-default mt-3 flex items-center justify-between">
        <div className="flex items-center gap-1">
          {search.straat && (
            <Button
              variant="secondary"
              size="small"
              icon={RegularXmark}
              iconPosition="right"
              asChild
            >
              <Link
                to="/"
                search={{ ...search, straat: undefined }}
                activeProps={{ className: '' }}
              >
                Straat: {search.straat}
              </Link>
            </Button>
          )}

          {search.postcode && (
            <Button
              variant="secondary"
              size="small"
              icon={RegularXmark}
              iconPosition="right"
              asChild
            >
              <Link
                to="/"
                search={{ ...search, postcode: undefined }}
                activeProps={{ className: '' }}
              >
                Postcode: {search.postcode}
              </Link>
            </Button>
          )}

          {search.plaats && (
            <Button
              variant="secondary"
              size="small"
              icon={RegularXmark}
              iconPosition="right"
              asChild
            >
              <Link
                to="/"
                search={{ ...search, plaats: undefined }}
                activeProps={{ className: '' }}
              >
                Plaats: {search.plaats}
              </Link>
            </Button>
          )}

          {search.locatienummer && (
            <Button
              variant="secondary"
              size="small"
              icon={RegularXmark}
              iconPosition="right"
              asChild
            >
              <Link
                to="/"
                search={{ ...search, locatienummer: undefined }}
                activeProps={{ className: '' }}
              >
                Locatienummer: {search.locatienummer}
              </Link>
            </Button>
          )}

          {hasSearch ? (
            <TextButton size="small" asChild>
              <Link activeProps={{ className: '' }} to="/" search={{ p: 1 }}>
                Alle zoektermen wissen
              </Link>
            </TextButton>
          ) : null}
        </div>

        <div>
          <ModalTrigger
            modalComponent={Search}
            onOpen={() =>
              pick(search, [
                'query',
                'straat',
                'postcode',
                'plaats',
                'locatienummer',
              ])
            }
            asChild
          >
            <TextButton size="small">Uitgebreid zoeken</TextButton>
          </ModalTrigger>
        </div>
      </div>

      <div className="container-default mt-3">
        <ul className="flex flex-col gap-1">
          {!bedrijven.data?.objects?.length ? (
            <li>
              <p className="style-body italic text-gray-800">
                Deze zoekterm(en) hebben geen resultaten opgeleverd.
              </p>
            </li>
          ) : (
            <>
              {bedrijven.data.objects.map((bedrijf) => {
                const {
                  ID,
                  Naam,
                  Plaatsnaam,
                  Locatienummer,
                  Postcode,
                  Straatnaam,
                  Huisnummer,
                } = bedrijf;

                if (!ID) return null;

                return (
                  <li key={bedrijf.ID}>
                    <CompanyRowLink
                      title={Naam ?? ''}
                      location={`${Straatnaam} ${Huisnummer}, ${Postcode} ${Plaatsnaam}`}
                      number={Locatienummer}
                      tenant={bedrijf.Tenant}
                      to="/$bedrijfLineageId/bedrijf/overzicht"
                      params={{
                        bedrijfLineageId: bedrijf.Lineage,
                      }}
                    />
                  </li>
                );
              })}

              {(bedrijven.data?.num_pages ?? 0) > 1 ? (
                <div className="mt-3 flex justify-center">
                  <PaginationLinks
                    aria-label="Bedrijven pagina's"
                    renderLink={({ children, page }) => (
                      <Link to="/" search={(prev) => ({ ...prev, p: page })}>
                        {children}
                      </Link>
                    )}
                    currentPage={search.p}
                    totalPages={bedrijven.data?.num_pages ?? 0}
                  />
                </div>
              ) : null}
            </>
          )}
        </ul>

        <Disclaimer className="mt-4 px-4" />
      </div>
    </>
  );
}

const SEARCH_INPUT_ID = 'search-for-company';
const SEARCH_LABEL = 'Zoek op bedrijfsnaam';

const SearchInput = ({
  onSubmit,
  defaultValue,
  isLoading,
}: {
  onSubmit: FormEventHandler<HTMLFormElement>;
  defaultValue: string;
  isLoading?: boolean;
}) => {
  return (
    <form onSubmit={onSubmit}>
      <div className="style-input-label">
        <FormLabel htmlFor={SEARCH_INPUT_ID} className="sr-only mb-1 block">
          {SEARCH_LABEL}
        </FormLabel>

        <div className="w-full">
          <div className="relative">
            <div className="inset-y absolute left-2 flex h-full items-center text-placeholder">
              <RegularMagnifyingGlass size={18} />
            </div>

            <input
              defaultValue={defaultValue}
              id={SEARCH_INPUT_ID}
              name={SEARCH_INPUT_ID}
              placeholder={SEARCH_LABEL}
              type="search"
              autoComplete="off"
              className={twJoin(
                'style-h4 h-8 w-full rounded-sm border border-white bg-white pl-6 pr-4 shadow-lg transition-all',
                'placeholder:text-placeholder',
                'focus:!border focus:!border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500',
                'disabled:cursor-not-allowed disabled:border-gray-300 disabled:bg-gray-300 disabled:text-gray-600'
              )}
            />

            <div className="absolute right-2 top-0 flex h-full items-center">
              <Button
                variant="tertiary"
                type="submit"
                state={isLoading ? 'loading' : undefined}
              >
                Zoeken
              </Button>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
};
